import { shallowReactive,shallowRef, ref, computed, Ref, Prop, ComputedRef } from "vue";
import Events from "../utils/Events"
import Attribute, { AttributeType } from "./Attribute";
import Reference from "./Reference";
import XMLRDBElement from "../xmlrdb-metadata/Element";
import ElementArray from "./ElementArray";
import RootElement from "./RootElement";
import {type TypedSubElements, default as SubElements} from "./SubElements";
// import DetailClass from "./DetailClass";

// Groß-Klein-Schreibung in Jackson-Konvention:
// - Bis zum ersten "_" wird der key klein geschrieben
// - Nach dem ersten "_" wird die Gross-/Kleinschreibung aus dem originalem key uebernommen
const toJackson =(key)=> {
  const parts = key.split(/_(.*)/s);  // Splittet nur am ersten Vorkommen
  let result = parts[0].toLowerCase();
  if (parts.length > 1)
    result += "_" + parts[1]
  return result;
}

export type DataElementAttributes = Record<string, AttributeType>;


export interface DataElementOptions<T extends DataElementAttributes = DataElementAttributes> {
  parent?: DataElement;
  nr?: number;
  depth?: number;
  attributes?: Partial<T>;
  elementArray?: ElementArray;
  metadata?: XMLRDBElement;
  isNew?: boolean;
  subElements?: SubElements;
  subElementMetadata?: Array<XMLRDBElement>;
}

export default class DataElement<T extends DataElementAttributes = DataElementAttributes> extends Events{

  /**
    -------------
    Persistence-Conditions werden aufgerufen um abzugfragen, ob ein Datensatz geschrieben werden soll.
    Persistence-Conditions können Elementen Attributen verankert werden. 
    Sie werden von DataElement.persistenceCondition ausgewertet.
     In PersistenceConditions kann eine Funktion registriert werden, die dann innerhalb DataElement.persistenceCondition
     ausgewertet wird.
     Was macht man um das ans Laufen zu bekommen?
     1. Tag in XMLRDB vereinbaren:
         <Tag name="PersistenceCondition" value="hasZuwendungsjahr"/>
     2. Funktion registrieren:
          Element.PersistenceConditions["EF_BEWILLIGUNG_JAHR.hasZuwendungsjahr"] = (dataElement) => {
               var kannGespeichertWerden = false;
               // .. Hier kommt Code hin, der das übergebene Dataelement prüft und kannGespeichertWerden berechnet
               return kannGespeichertWerden
          } 
     ----------
     Zwei Standard-PersistenceConditions werden immer registriert:
     "false": Speichere dieses DataElemnt niemals ab
     "hasChildren": Speicher DataElemnt nur dann ab, wenn es ein abzuspeicherndes Child mit dem Tag PersistenceConditionChild gibt
  */
  static PersistenceConditions =  {
    "false": dataElement => false,
    hasChildren: dataElement => {
      for (const childElement of dataElement.metadata.children) {
        if (childElement.tagByName("PersistenceConditionChild")) {
          // In den (ungeloeschten) Elementen
          const visibleChildren =dataElement.__[childElement.name()].visibleDataElements;
          // Gibt es Element, das gespeichert werden muss?
          const hasPersistenceVisibleChildren = visibleChildren.value.some(c => c.persistenceCondition.value);  
          if (hasPersistenceVisibleChildren ) {
            return true;
          }
        }
      }
      return false;
    }
  };

  parent: DataElement | null;
  nr: number | null;
  depth: number | null;
  metadata: XMLRDBElement | null | undefined;
  extension: Record<string, unknown>;
  
  attributes: Record<keyof T, Attribute<T[keyof T]>  | Reference<T[keyof T]>>;
  $: T
  $$: {
    [K in keyof T]: Attribute<T[keyof T]>  | Reference<T[keyof T]>;
  };

  elementArray: ElementArray | undefined;
  subElements: SubElements;

  __: TypedSubElements;
  _: Record<string, DataElement>;

  isVisible = computed(() => {
    return this.metadata?.isVisible(this);
  });

  isEditable: ComputedRef<boolean> = computed<boolean>(() => {
    let editableToggler: DataElement = this.metadata?.tagByName("breakEditableInheritance") ? this.root() : this.parent;
    if (editableToggler)
      return editableToggler.isEditable.value;
    else
      return false;
  });

  isDeleted: Ref<boolean>;
  isNew: Ref<boolean>;
  selfHasChanged: ComputedRef<boolean>;
  hasChanged: ComputedRef<boolean>;
  selfOnChangeReenterPassword: ComputedRef<boolean>;
  onChangeReenterPassword: ComputedRef<boolean>;
  selfHasError: ComputedRef<boolean>;
  hasError: ComputedRef<boolean>;
  persistenceCondition: Ref<boolean>;
  ownErrors: (maxError?: number) => string[];
  extraData: Ref<Record<string, unknown>> = ref({});

  id = computed(() => {
    const ascPKAttrs = this.metadata?.getPrimaryKeyAttributes();
    ascPKAttrs.sort((a, b) => a.name().localeCompare(b.name()));
    return ascPKAttrs.map(a => this.$[a.name()]).join(";");
  });

  constructor(options: DataElementOptions) {
    super();
    this.parent = options.parent || null;
    this.nr = options.nr || null;
    this.depth = options.depth || null;

    this.attributes = {} as Record<keyof T, Attribute<T[keyof T]>  | Reference<T[keyof T]>>;
    this.metadata = options.metadata;
    this.extension = {};
    /**
     *  der Proxy erlaubt einen vereinfachten Zugriff auf Attributes.
     *  Statt:
     *    this.attributes.bemerkung.value
     *
     *  können wir schreiben:
     *    this.$.bemerkung
     */
    
    // Create a type-safe proxy for accessing attribute values
    this.$ = new Proxy(this.attributes as any, {
      get: (target: any, prop: string | symbol) => {
        if (typeof prop === "symbol") return undefined;
        
        const key = prop as keyof T;
        if (!Object.prototype.hasOwnProperty.call(target, key)) {
          console.error(`${this?.metadata?.name?.()}: Prop ${String(prop)} not found`);
          return undefined;
        }
        return target[key]?.ref?.value;
      },
      set: (target: any, prop: string | symbol, value: any) => {
        if (typeof prop === "symbol") return false;
        
        const key = prop as keyof T;
        if(target[key]) {
          target[key].ref.value = value;
        }
        return true;
      }
    }) as T;

    // Create a proxy for accessing attribute objects
    this.$$ = new Proxy(this.attributes as any, {
      get: (target: any, prop: string | symbol) => {
        if (typeof prop === "symbol") return undefined;
        
        const key = prop as keyof T;
        if (!target[key] && typeof prop !== "symbol") {
          console.error(`${this?.metadata?.name?.()}: Prop ${String(prop)} not found`);
        }
        return target[key];
      }
    }) as { [Property in keyof T]: Attribute<T[Property]> | Reference<T[keyof T]>};

    if (this.metadata) {
      const unconvertedAttributes = Object.keys(options.attributes || {}).filter(key => !this.metadata.allAttributes.some(attr => attr.name() === key));
      if (unconvertedAttributes.length > 0) {
        this.extraData.value = {};
        for( const key of unconvertedAttributes) {
          this.extraData.value[key] = options.attributes?.[key];
        }
      }
      for (let attr of this.metadata?.allAttributes) {
        const attrName = attr.name() as keyof T;
        let keyValue = options.attributes?.[attr.name()] as T[keyof T];
        if (attr.type() === "Reference" || attr.type() === "ajaxreference") {
          this.attributes[attrName] = new Reference(keyValue, {
            metadata: attr,
            dataElement: this
          });
        }
        else {
          this.attributes[attrName] = new Attribute(keyValue, {
            metadata: attr,
            dataElement: this
          });
        }
      }
    }

    this.isDeleted = ref(false);
    this.isNew = ref(options.isNew || false);
    this.subElements = options.subElements ?? new SubElements(this.metadata?.children ?? options.subElementMetadata, this);
    this.__ = this.subElements;
    // Shortcut-Zugriff auf's erste Element
    this._ = new Proxy(this.subElements, {
      get(target, prop, receiver) {
        if(typeof prop === "symbol") return undefined;
        return target[prop]?.dataElements?.[0];
      }
    }) as unknown as Record<string, DataElement>; 
 
    this.elementArray = options.elementArray;

    /**
     * Soll der Datensatz für's XML-Erzeugen berücksichtigt werden.
     * Erklärung dazu in [../../doku/DataElement-selfHasChanged.xlsx]
     */
    this.selfHasChanged = computed<boolean>(() => {
      // Datensatz anlegen und wieder weggeworfen -> Der ist so gut wie nicht da
      if (this.isDeleted.value && this.isNew.value) return false;

      // Löschanforderung eines **bestehenden** Datensatzes soll auf jeden Fall geschrieben werden
      if (this.isDeleted.value) return true;
      
      // Ab jetzt: **Keine** Löschanforderung für Datensatz

      // Neue Datensätze ohne Einwände der PC: Speichern
      if (this.isNew.value && this.persistenceCondition.value) return true;

      // Neue Datensätze gegen Einwände der PC nicht speichern
      if (this.isNew.value && !this.persistenceCondition.value) return false;

      // Jetzt gilt: Datensatz existiert schon in DB und es besteht keine Löschanforderung
      // Falls die persistenceCondition das verhindern will, wäre es egal. Grund: Die DB muss vom Änderungswunsch wissen
      // Beim Erzeugen vom XML wird der Fall persistenceCondition===false ggf. in ein Delete umgesetzt
      const attributesChanged = this.metadata?.allAttributes.some(attr => this.attributes[attr.name()].hasChanged.value) ?? false;
      return attributesChanged;
    });

    this.hasChanged = computed(() => {
      // @ts-ignore
      return this.selfHasChanged.value || this.subElements.hasChanged.value;
    })

    this.selfOnChangeReenterPassword = computed(() => {
      if (this.metadata?.onChangeReenterPassword(this)) {
        return true;
      }
      if (this.metadata?.allAttributes.some(attr => this.attributes[attr.name()].onChangeReenterPassword.value)) {
        return true;
      }
      return false;
    });

    this.onChangeReenterPassword = computed(() => {
      if (this.selfOnChangeReenterPassword.value) {
        return true;
      }
      let it = this.makeChildrenIterator();
      for (const dataElement of it) {
        if (dataElement.onChangeReenterPassword.value) {
          return true;
        }
      }
      return false;
    });

    this.selfHasError = computed(() => {
      const result =this.metadata?.allAttributes.some(a => this.$$[a.name()].hasError.value) ?? false;
      // DEBUG-Tipp: Wenn ein Speicher-Knopf ausgegraut ist, weil ein Datalement einen Fehler hat, frat man sich schon mal, wo der Fehler herkommt.
      // Nachfolgender Code soll dan auskommentiert werden.
      //if (result) {
      //   console.log("hasError", this.selfHasError.value);
      //   const errAttr = this.metadata?.allAttributes.filter(a => this.$$[a.name()].hasError.value)
      //   errAttr.forEach(a => console.log("hasError",a.name(),this.$$[a.name()].errors.value));
      //}
      return result;
    })

    this.hasError = computed(() => {
      // @ts-ignore
      const result = this.selfHasError.value || this.subElements.keys.some(e => this.subElements[e].hasError.value);
     // DEBUG-Tipp: Wenn ein Speicher-Knopf ausgegraut ist, weil ein Datalement einen Fehler hat, frat man sich schon mal, wo der Fehler herkommt.
     // Nachfolgender Code soll dan auskommentiert werden.
     //if (result) {
     //  console.log("hasError", this.selfHasError.value);
     //  const errAttr = this.metadata?.allAttributes.filter(a => this.$$[a.name()].hasError.value)
     //  errAttr.forEach(a => console.log("hasError",a.name(),this.$$[a.name()].errors.value));
     //}
      return result;
    })

    /*
       Berechnet, ob eine PersistenceCondition (am DataElement oder an einem Attribut) zutrifft.
       Falls keine PersistenceCondition vorhanden ist, wird ebenfalls true zurückgeloiefert.
       - persistenceCondition==true ->  Die PC hat keine Einwände gegen das Speichern
       - persistenceCondition==false -> Die PC untersagt das Speichern
    */
    this.persistenceCondition = computed(() => {
      let elementPersistenceConditions = this.metadata?.tagValuesByName("PersistenceCondition");
      let persAttrs = this.metadata?.allAttributes.filter(a => a.tagValueByName("PersistenceCondition"));
      
      if (!persAttrs) persAttrs = [];
      if (!elementPersistenceConditions) elementPersistenceConditions = [];

      if (persAttrs.length == 0 && elementPersistenceConditions.length == 0 ) {
        return true;  // Keine PC vorhanden -> Keine Einwände gegen Speichern
      }

      for (const attr of persAttrs) {
        const op = attr.tagValueByName("PersistenceCondition")
        if (op === "truthy") {
          if (this.$[attr.name()]) {
            return true;
          }
        }
        else if (op === "notnull") {
          if (this.$[attr.name()] !== null && this.$[attr.name()] !== undefined) {
            return true
          }
        }
      }
      for (const pc of elementPersistenceConditions) {
        if (DataElement.PersistenceConditions[pc]) {
          if (DataElement.PersistenceConditions[pc](this))
            return true;
        }
        else {
          console.error("Unknown PersistenceCondition '" + pc + "' on Elemenclass '" + this.metadata.name() + "'");
        }
      }
      return false;
    });

    this.ownErrors = (maxError) => {
      const result = [];
      if (maxError === undefined) maxError = 20;
      let unshownErrors = 0;
      this.metadata?.allAttributes.forEach(a => {
        this.$$[a.name()].errors.value?.forEach(e => {
          if (result.length<maxError) result.push(e);
          else  unshownErrors++;
        })
      });
      if (unshownErrors>0) result.push(`${unshownErrors} weitere Fehler`)
      //console.debug(this.subElements.keys.length);
      return result;
    }
  }

  * makeChildrenIterator() {
    for (const key of this.subElements.keys) {
      for (const dataElement of this.subElements[key].dataElements) {
        yield dataElement;
      }
    }
  }

  path() {
    let path = [...(this.elementArray?.path() ?? [])];
    path.push(this.nr);
    return path;
  }
  namedpath() {
    let namedpath = [...(this.elementArray?.path() ?? [])];
    namedpath.push(this.metadata.name());
    return namedpath;
  }

  /**
   *
   * @param {DataElement} subElement
   */
  addSubElement(metadata, subElement, silent) {
    subElement.depth = this.depth + 1;
    subElement.parent = this;
    // @ts-ignore
    this.subElements.add(metadata, subElement, silent)
  }

  root() {
    let pivot: RootElement | DataElement = this;
    while (pivot.parent) {
      pivot = pivot.parent;
    }
    return pivot as RootElement;
  }

  /**
   * level == 0 liefert this
   * level == 1 liefert direkt parent DataElement
   * ...
   * @returns {DataElement}
   */
  parentByLevel(level) {
    let pivot = this;
    while (pivot.parent && level-- > 0) {
      // @ts-ignore
      pivot = pivot.parent;
    }
    return pivot;
  }

  // Kopiere alle Attribute nach Javascript.
  // Alle Attribute werden dabei KLEIN geschrieben
  copyToObject() {
    const target = {};
    for (let key in this.$) {
      target[key.toLowerCase()] = this.$[key];
    }
    return target;    
  }

  // Kopiere alle Attribute nach Javascript.
  // Groß-Klein-Schreibung wird 1:1 übernommen
  copyToPureJSObject() {
    const target = {} as T;
    for (let key in this.$) {
      target[key] = this.$[key];
    }
    return target;    
  }

  // Kopiere alle Attribute nach Javascript.
  // Groß-Klein-Schreibung in Jackson-Konvention
  copyToJackson() {
    const target = {};
    for (let key in this.$) {
      target[toJackson(key)] = this.$[key];
    }
    return target;    
  }

  copy() {
    const newDataElement = new DataElement({
      parent: this.parent,
      metadata: this.metadata,
      attributes: this.$,
      // subElements: this.subElements // attributes: this.$ ist ok, da die Attributwerte immer kopiert werden
    });
    for (const key in newDataElement.$$) {
      newDataElement.$$[key].initialValue.value = this.$$[key].initialValue.value;

    }
    // @ts-ignore
    for (const key of this.subElements.keys) {
      this.__[key].dataElements.forEach(d => {
        newDataElement.__[key].insertElement(d.copy());
      });
    }
    for (const key in newDataElement.$$) {
      // wenn wir kein FullLookup haben, müssen die selektierten Werte auch mit rüber kopiert werden, da sonst kein Label gebildet werden kann
      if(this.$$[key] instanceof Reference) {
        newDataElement.$$[key].selected.value = this.$$[key].selected.value;
      }
    }

    newDataElement.isNew.value = this.isNew.value;
    newDataElement.isDeleted.value = this.isDeleted.value;
    return newDataElement;
  }

  /**
   * 
   * @param {DataElement} dataElement 
   */
  merge(dataElement) {
    if (!dataElement) {
      this.elementArray.removeElement(this);
      return;
    }

    this.isNew.value = dataElement.isNew.value;
    this.isDeleted.value = dataElement.isDeleted.value;

    for (let key in this.$) {
      const fattr = dataElement.$$[key];
      if (this.$$[key] instanceof Reference) {
        if(this.$[key] !== fattr.value)
          this.$$[key].selected.value = fattr.selected.value;
      }
      else {
        if( this.$[key] !== fattr.value)
          this.$[key] = fattr.value;
      }
      if(this.$$[key].initialValue.value !== fattr.initialValue.value)
        this.$$[key].initialValue.value = fattr.initialValue.value;
    }
    // @ts-ignore
    for (const key of this.subElements.keys) {
      this.__[key].merge(dataElement.__[key]);
    }
  }

  update(attributes) {
    this.dispatch("beforeupdate", this.attributes);
    this.isDeleted.value = false;
    this.isNew.value = false;
    for (let key in this.attributes) {
      let value = this.attributes[key].metadata.parseValue(attributes[key]);
      this.attributes[key].initialValue.value = value;
      this.attributes[key].value = value;
    }
    this.dispatch("update", this.attributes);
  }

  delete() {
    this.elementArray.delete(this);
  }

  toString() {
    return "";
  }

  async acceptChanges() {
    if (this.isNew.value) {
      this.isNew.value = false;
    }

    if (this.isDeleted.value) {
      this.elementArray.removeElement(this);
    }

    for (const key in this.attributes) {
      this.attributes[key].acceptChange()
    }
  }

  resetChanges() {
    this.isDeleted.value = false;
    for (const key in this.attributes) {
      this.attributes[key].reset();
    }
  }
}